import { useState } from 'react';
import { extractTextFromPDF } from '../utils/pdf';

export default function TestPDF() {
  const [file, setFile] = useState<File | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string>('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setResult(null);
      setError('');
    } else {
      setError('请选择PDF文件');
    }
  };

  const handleExtract = async () => {
    if (!file) return;

    setExtracting(true);
    setError('');
    setResult(null);

    try {
      console.log('开始提取PDF文本...');
      const startTime = Date.now();
      
      const pdfInfo = await extractTextFromPDF(file);
      
      const endTime = Date.now();
      const duration = ((endTime - startTime) / 1000).toFixed(2);

      console.log('提取完成!', pdfInfo);
      
      setResult({
        ...pdfInfo,
        duration,
        textLength: pdfInfo.text.length,
        textPreview: pdfInfo.text.substring(0, 500)
      });
    } catch (err: any) {
      console.error('提取失败:', err);
      setError(err.message || '提取失败');
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">PDF文本提取测试</h1>

        {/* 文件选择 */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">1. 选择PDF文件</h2>
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-full file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100"
          />
          {file && (
            <div className="mt-4 p-4 bg-gray-50 rounded">
              <p className="text-sm"><strong>文件名:</strong> {file.name}</p>
              <p className="text-sm"><strong>大小:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          )}
        </div>

        {/* 提取按钮 */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">2. 提取文本</h2>
          <button
            onClick={handleExtract}
            disabled={!file || extracting}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium
              hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed
              transition-colors"
          >
            {extracting ? '提取中...' : '开始提取'}
          </button>
        </div>

        {/* 错误信息 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* 提取结果 */}
        {result && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">3. 提取结果</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 rounded">
                  <p className="text-sm text-gray-600">总页数</p>
                  <p className="text-2xl font-bold text-green-700">{result.totalPages}</p>
                </div>
                <div className="p-4 bg-blue-50 rounded">
                  <p className="text-sm text-gray-600">文本长度</p>
                  <p className="text-2xl font-bold text-blue-700">{result.textLength.toLocaleString()}</p>
                </div>
                <div className="p-4 bg-purple-50 rounded">
                  <p className="text-sm text-gray-600">提取耗时</p>
                  <p className="text-2xl font-bold text-purple-700">{result.duration}秒</p>
                </div>
                <div className="p-4 bg-yellow-50 rounded">
                  <p className="text-sm text-gray-600">平均每页</p>
                  <p className="text-2xl font-bold text-yellow-700">
                    {(result.textLength / result.totalPages).toFixed(0)}字符
                  </p>
                </div>
              </div>

              {/* 元数据 */}
              {result.metadata && (
                <div className="p-4 bg-gray-50 rounded">
                  <h3 className="font-semibold mb-2">PDF元数据</h3>
                  <pre className="text-xs overflow-auto">
                    {JSON.stringify(result.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {/* 文本预览 */}
              <div className="p-4 bg-gray-50 rounded">
                <h3 className="font-semibold mb-2">文本预览 (前500字符)</h3>
                <p className="text-sm whitespace-pre-wrap font-mono">
                  {result.textPreview}...
                </p>
              </div>

              {/* 完整文本 */}
              <details className="p-4 bg-gray-50 rounded">
                <summary className="font-semibold cursor-pointer">查看完整文本</summary>
                <pre className="mt-4 text-xs whitespace-pre-wrap overflow-auto max-h-96">
                  {result.text}
                </pre>
              </details>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
